import axios from 'axios';
import { renderCrop } from './partCrop';
import { type TipPicture } from './PromptBadge';
import type { NoticeInfo } from './realImages';

// Finding the listing links from the manual itself, instead of pasting them.
//
// The manual carries both halves of the question: the model name on the cover, and
// the shop's own web address in the customer-service block. The server does the
// looking-up (a grounded model) and, crucially, FETCHES every address it is given
// before offering it — so what comes back here is a list of pages that exist. See
// /api/notice/listing-suggest.

// One suggestion: a page the server just fetched, with what the page calls itself
// and the picture it declares for itself.
export type ListingCandidate = { url: string; title: string; image: string };

// ONE MODEL CALL, as the badge walks it: the match, which looks at the shop's pictures.
// What to search the shop WITH was read by Phase 1's sections call (the brand store's
// `site` and `queries`), so there is no read call here any more. A list still, since a
// search with nothing to look at makes none.
//
// `urls` are the pictures the SERVER fetched for the call: the candidate thumbnails.
export type ListingCall = {
  kind: string;
  prompt: string;
  model: string;
  raw: string;
  reasoning: string;
  urls: string[];
};

// One page of the manual as the search sends it: its number and its render.
export type SuggestPage = { n: number; image: string };

// What one press of the button did: the links it proposes, every call it paid for, and the
// pages it sent — kept, because the server records none of them (they were posted up, not
// fetched), and the badges show every picture a call carried.
export type ListingSearch = {
  listings: ListingCandidate[]; calls: ListingCall[]; pages: SuggestPage[];
  // Why the shop gave nothing, when that was not its answer: 'refused:403', 'unreachable',
  // 'notShopify', 'siteDown', 'noQueries'. '' when it answered.
  searchError: string;
};

// Thrown when there is no drawing of the product to match: the read found none, or it
// would not render. Said as itself rather than as "the search failed".
export const NO_PAGES_ERROR = 'NO_PAGES';
// Thrown when the manual has not been read yet: there is no address to search.
export const NO_INFO_ERROR = 'NO_INFO';

// The question this search would ask, before it has asked it — what the badge shows on a
// phase nobody has run yet, exactly as every other prompt badge in the app does it
// (`preview: true` to the route that would run the call).
export async function previewCalls(): Promise<ListingCall[]> {
  const { data } = await axios.post<{ calls?: (Partial<ListingCall> & { label?: string })[] }>(
    '/api/notice/listing-suggest', { preview: true },
  );
  return asCalls(data.calls);
}

const asCalls = (raw: (Partial<ListingCall> & { label?: string })[] | undefined): ListingCall[] =>
  (raw || []).map((c) => ({
    kind: c.label || '',
    prompt: c.prompt || '',
    model: c.model || '',
    raw: c.raw || '',
    reasoning: c.reasoning || '',
    urls: Array.isArray(c.urls) ? c.urls.filter((u): u is string => !!u) : [],
  }));

// The finished product as the search sends it: cut out of the manual where Phase 1's read
// found it drawn, so the match compares the product and not the parts list beside it on the
// cover. Also what the badge shows before any search has run. Empty when the read found no
// such picture.
export async function renderProduct(
  file: Blob, info: Pick<NoticeInfo, 'heroPage' | 'heroBox'>,
): Promise<SuggestPage[]> {
  if (!info.heroPage || !info.heroBox) return [];
  const image = await renderCrop(file, info.heroPage, info.heroBox);
  return image ? [{ n: info.heroPage, image }] : [];
}

// EVERY PICTURE THE CALL CARRIED, in the order it carried them: the product, then the shop's
// thumbnails, which the server records, since it fetched them.
export function callPictures(
  pages: SuggestPage[], thumbs: string[], t: (k: string) => string,
): TipPicture[] {
  return [
    ...pages.slice(0, 1).map((p) => ({ url: p.image, label: t('notice.listing.productDrawing') })),
    ...thumbs.map((url) => ({ url })),
  ];
}

// Ask the server to find this manual's listings. Returns the ones it matched, best
// first, and the call it made; an empty list means it found none, which is a normal
// answer — and the call comes back anyway, since "why did it find nothing" is exactly
// the question the badge answers.
export async function suggestListings(
  file: Blob, info: Pick<NoticeInfo, 'site' | 'productName' | 'queries' | 'heroPage' | 'heroBox'>,
): Promise<ListingSearch> {
  if (!info.site.trim()) throw new Error(NO_INFO_ERROR);
  const pages = await renderProduct(file, info);
  if (!pages.length) throw new Error(NO_PAGES_ERROR);
  const { data } = await axios.post<{
    listings?: ListingCandidate[];
    calls?: (Partial<ListingCall> & { label?: string })[];
    searchError?: string;
  }>('/api/notice/listing-suggest', {
    image: pages[0].image, site: info.site, productName: info.productName, queries: info.queries,
  });
  return {
    listings: (data.listings || []).filter((l) => l && typeof l.url === 'string' && l.url),
    calls: asCalls(data.calls),
    pages,
    searchError: data.searchError || '',
  };
}
